import { GridRowModel, GridValueFormatterParams, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { updateUserAccessMessage } from 'src/components/common/AccessMessage';
import { AppState, store } from 'src/store';
import { setDocumentDetail } from 'src/store/documentDetail';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setLoader } from 'src/store/Loader';
import { setRowDetail } from 'src/store/rowGridData';
import { setUserDocPermissions } from 'src/store/User';
import { Translation } from 'src/types/Translation';
import { updateUserAccess } from 'src/util/util';
import { ISelected } from '../common/hierarchy/SearchHierarchy';
import { usePermissionWrapper } from '../common/hooks/usePermissionWrapper';
import { useTranslations } from '../common/hooks/useTranslations';
import DocManagementService from '../eTMF/documentUpload/service/DocManagementService';
import { useGetAllBinderViewerQuery } from '../Services/BinderViewer';
import { masterDocumentApi, useGetAllMasterDocumentQuery } from '../Services/MasterDocument';
import {
  IDocument,
  IDocumentProperties,
  ITreeItemFirstLevelBinderView,
  ITreeItemParentBinderView,
  ITreeItemSecondLevelBinderView
} from './types';
import { getLevel, getParentNodes, getQuery, getTreeItems, notInArray } from './util';
import { IViewer } from './Viewer';
export interface ILevel {
  firstlevel: ITreeItemParentBinderView | undefined;
  secondlevel: ITreeItemFirstLevelBinderView | undefined;
  thirdlevel: ITreeItemSecondLevelBinderView | undefined;
}

export const useViewer = (props: IViewer) => {
  const dispatch = useAppDispatch();
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const binderViewTitle = useTranslations({} as Translation, {}, 'binderViewTitle').binderViewTitle;
  const recentDocumentsHeading = useTranslations({} as Translation, {}, 'recentDocuments').recentDocuments;
  const [expanded, setExpanded] = useState<string[]>([]);
  const [hierachyLoading, setHierachyLoading] = useState<boolean>(false);
  const [documentToDisplay, setDocumentToDisplay] = useState<IDocumentProperties | undefined>();
  const [dataGridComponent, setDataGridComponent] = useState(true);
  const [selectedRowDocumentDetails, setSelectedRowDocumentDetails] = useState<IDocumentProperties | undefined>();
  const [openAuditTrail, setOpenAuditTrail] = useState(false);
  const [openVersionHistory, setOpenVersionHistory] = useState(false);
  const [openSendAsLinkDialog, setSendAsLinkDialog] = useState(false);
  const [docUploadComponent, setDocUploadComponent] = useState(true);
  const SearchHierarchyDetail: any = useSelector((state: AppState) => state.searchHierarchyDetails);
  const [artifactList, setArtifactList] = useState<string[]>([]);
  const [fileId, setFileId] = useState('');
  const user: any = useAppSelector(state => state.user.user);
  const Sponsor = user?.sponsor ? user.sponsor.toUpperCase() : '';
  const [menu, selectedMenu] = useState<ISelected>({
    sponsor: Sponsor,
    study: SearchHierarchyDetail?.data[0]?.Study ? SearchHierarchyDetail?.data[0]?.Study : '',
    country: SearchHierarchyDetail?.data[0]?.Country ? SearchHierarchyDetail?.data[0]?.Country : '',
    site: SearchHierarchyDetail?.data[0]?.Site ? SearchHierarchyDetail?.data[0]?.Site : ''
  });
  const deletedId = documentDetail.data[0]?.deletedId;
  const { data, isLoading, error } = useGetAllBinderViewerQuery();
  const {
    data: recentDocuments,
    isLoading: recentDocumentLoading,
    error: recentDocumentError,
    refetch: getRecentDocs
  } = useGetAllMasterDocumentQuery('?is_binder_view=true&user_based_details=true&limit=10');

  const [level, setLevels] = useState<ILevel | undefined>(undefined);
  const [selection, setSelection] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [pageNumber, setPageNumber] = React.useState<number>(0);
  const [rowCount, setRowsCount] = React.useState<number>(0);
  const apiRef = useGridApiRef();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const density = useAppSelector(state => state.binderViewSettings.density);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
    tmf_artifact_name: false,
    tmf_doc_level: false,
    tmf_doc_study: false,
    tmf_doc_country: false,
    tmf_doc_site: false,
    tmf_doc_version_number: false,
    tmf_doc_viewer_req_certified_copy: false,
    created_on: false
  });
  const documentsResult = React.useMemo(
    () => masterDocumentApi.endpoints.getAllMasterDocument.select(selection as string),
    [selection]
  );
  const { refetch: getAllDoc } = useGetAllMasterDocumentQuery(selection as string);

  const { isLoading: documentsLoading, data: documents, error: documentsError } = useAppSelector(documentsResult);

  useEffect(() => {
    if (selection) {
      const result = dispatch(masterDocumentApi.endpoints.getAllMasterDocument.initiate(selection));
      return result.unsubscribe;
    }
  }, [dispatch, selection]);

  useEffect(() => {
    if (level) {
      if (pageSize === 10 || pageSize === 20 || pageSize === 30) {
        if (pageNumber * pageSize > rowCount - 1) {
          setPageNumber(0);
          setSelection(getQuery(level, menu, 0, pageSize));
        } else setSelection(getQuery(level, menu, pageNumber * pageSize, pageSize));
      }
    }
  }, [level, menu, pageNumber, pageSize]);

  useEffect(() => {
    let route = 'binder_viewer';
    if (dataGridComponent || props.onClicked) {
      route = '';
      if (!dataGridComponent) {
        getAllDoc();
        getRecentDocs();
        setDataGridComponent(true);
      }
    }
    props.routeInfo && props.routeInfo(route);
  }, [dataGridComponent, props.onClicked]);

  useEffect(() => {
    dispatch(setLoader(isLoading || documentsLoading || recentDocumentLoading || hierachyLoading));
  }, [isLoading, documentsLoading, recentDocumentLoading, hierachyLoading]);

  const treeViewData = useMemo(() => {
    if (data) return getTreeItems(data);
    return [];
  }, [data]);

  useEffect(() => {
    window.scroll(0, 0);
    return () => {
      props.routeInfo && props.routeInfo(null as any);
    };
  }, []);

  const getFormattedDocument = (item: IDocument): IDocumentProperties => {
    return {
      ...item.file_master,
      ...item.document_data,
      id: item.document_data.id,
      hashed_uri_key: item.hashed_uri_key,
      fmId: item.file_master.id,
      fileId: item.file_master.id,
      docFormat: item.file_master.tmf_doc_type,
      tmf_doc_version_number: parseFloat(item.document_data.tmf_doc_version_number as string).toFixed(1) as string,
      need_to_sent_to_isf: item.need_to_sent_to_isf
    };
  };

  const items = useMemo(() => {
    let formatted: IDocumentProperties[] = [];
    if (documents) {
      const filtered = documents?.selected_data?.filter(
        (item: any) => item.document_data.tmf_document_id !== deletedId
      );
      formatted = filtered.map((item: any) => {
        return getFormattedDocument(item);
      });
    }
    setRowsCount(documents?.record_count);
    return formatted;
  }, [documents, dataGridComponent, props.onClicked]);

  useEffect(() => {
    if (documents) {
      setArtifactList(documents?.selected_data?.map((item: any) => item.document_data.tmf_artifact_name));
    }
  }, [items]);

  useEffect(() => {
    updateUserAccess(error, dispatch);
  }, [error]);

  useEffect(() => {
    updateUserAccess(documentsError, dispatch);
  }, [documentsError]);

  useEffect(() => {
    updateUserAccess(recentDocumentError, dispatch);
  }, [recentDocumentError]);

  const toggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
    const newExpanded = notInArray(nodeIds, expanded);
    const newParentNodes = getParentNodes(newExpanded);
    if (newParentNodes.length > 0) {
      const existingParentNodes = getParentNodes(expanded);
      const excludeExistingParents = notInArray(expanded, existingParentNodes);
      setExpanded([...excludeExistingParents, ...newParentNodes]);
    } else {
      setExpanded(nodeIds);
    }
  };

  const scroll = () => {
    window.scrollTo(0, 0);
  };

  const treeItemClick = (nodeId: string) => {
    const newLevel = getLevel(nodeId, data);
    setLevels(newLevel);
    if (newLevel.secondlevel) scroll();
  };

  const recentDocumentClick = (item: IDocument) => {
    setDocumentDetails(getFormattedDocument(item));
  };

  /**
   * Bharath
   */
  const docService = new DocManagementService();
  const setDocumentDetails = async (document: IDocumentProperties) => {
    try {
      const response: any = await docService.getDocAccessPermissions(document);
      if (response.data != null && response.data.length > 0) {
        const docAccessPermissions: any = response.data.map((item: any) => item.permission);
        dispatch(setUserDocPermissions(docAccessPermissions));
        setDocumentToDisplay(document);
        setDocumentDetail(dispatch, [document]);
        setDataGridComponent(false);
      } else {
        updateUserAccessMessage({ status: 403, dispatch: store.dispatch });
      }
    } catch (e: any) {
      updateUserAccessMessage({ status: 403, dispatch: store.dispatch });
    }
  };
  /**
   * Bharath end
   */

  const gethashedurikey = (params: GridValueFormatterParams<any>) => {
    return items.find(item => item.id === params.id)?.hashed_uri_key;
  };

  const onGridLinkClick = (row: GridRowModel) => {
    setDocumentDetails(row as IDocumentProperties);
  };

  /**
   * Bharath
   */
  const [currentRow, setCurrentRow] = useState<any>();
  const handleClick = async (e: React.MouseEvent<HTMLElement>, row: any) => {
    setDocumentDetail(dispatch, [row]);
    setRowDetail(dispatch, [row]);
    setSelectedRowDocumentDetails(row);
    setAnchorEl(e.currentTarget);
    setCurrentRow(row);
  };
  const { checkPermissionDocViewLevel, checkPermission } = usePermissionWrapper();
  const handleClose = async (optionMenuItem: { id: string; name: string; isEnabled: boolean }) => {
    try {
      setAnchorEl(null);
      if (optionMenuItem.id === 'auditTrail' && checkPermission('view_audit_history')) {
        setOpenAuditTrail(true);
      } else if (optionMenuItem.id === 'versionHistory' && checkPermission('view_version_history')) {
        setOpenVersionHistory(true);
      } else if (optionMenuItem.id === 'sendAsLink' && checkPermission('send_document_as_link')) {
        setSendAsLinkDialog(true);
      } else if (optionMenuItem.id === 'addNewVersion' && checkPermission('uploaded_document')) {
        setDocUploadComponent(false);
        setRowDetail(dispatch, [selectedRowDocumentDetails]);
      }
    } catch (e: any) {
      updateUserAccessMessage({ status: 403, dispatch: store.dispatch });
      setAnchorEl(null);
    }
  };
  /**
   * Bharath modification end
   */
  const getSelectedData = (menu: ISelected) => {
    selectedMenu(menu);
  };

  const getLoadingState = (isLoading: boolean) => {
    setHierachyLoading(false);
  };

  return {
    expanded,
    toggle,
    binderViewTitle,
    treeItemClick,
    treeViewData,
    recentDocuments,
    recentDocumentClick,
    recentDocumentsHeading,
    apiRef,
    documents,
    pageSize,
    setPageSize,
    setPageNumber,
    pageNumber,
    rowCount,
    density,
    columnVisibilityModel,
    setColumnVisibilityModel,
    getSelectedData,
    getLoadingState,
    items,
    documentToDisplay,
    dataGridComponent,
    gethashedurikey,
    onGridLinkClick,
    handleClick,
    anchorEl,
    handleClose,
    openAuditTrail,
    setOpenAuditTrail,
    setAnchorEl,
    openVersionHistory,
    setOpenVersionHistory,
    openSendAsLinkDialog,
    setSendAsLinkDialog,
    setDocumentDetail,
    setDocumentToDisplay,
    artifactList,
    fileId,
    setFileId,
    setDataGridComponent,
    selectedRowDocumentDetails,
    setSelectedRowDocumentDetails,
    getRecentDocs,
    docUploadComponent,
    setDocUploadComponent,
    getAllDoc
  };
};
